import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '~/core/utils/cn';
type LabelType = 'normal' | 'required' | 'optional';
type Variant = 'primary' | 'secondary';
export interface CustomInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  labelType?: LabelType;
  variant?: Variant;
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  helperText?: string;
  error?: boolean;
  errorMessage?: string | null;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  labelClassName?: string;
  containerClassName?: string;
  id?: string;
  disableSanitize?: boolean;
}
import Eye from '~/shared/components/icons/eye';
import EyeOff from '~/shared/components/icons/eye_off';
const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>(
  (
    {
      name,
      label,
      labelType = 'normal',
      variant = 'primary',
      placeholder,
      defaultValue,
      value,
      onChange,
      helperText,
      error,
      errorMessage,
      startAdornment,
      endAdornment,
      labelClassName,
      containerClassName,
      id,
      className,
      type = 'text',
      disableSanitize = false,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const { t } = useTranslation(['common']);
    const [showPassword, setShowPassword] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState<string>(defaultValue || '');

    // Keep internal state in sync when controlled `value` is passed
    React.useEffect(() => {
      if (value !== undefined) {
        setInternalValue(value);
      }
    }, [value]);

    const sanitizeInput = (value: string, type: string, skip?: boolean) => {
      if (skip) return value.replace(/^[ ]+/, '');
      if (type === 'password') {
        return (
          value
            .replace(/\s+/g, '') // remove all spaces
            // Remove common emoji Unicode ranges without touching digits or normal symbols
            .replace(/[\u{1F600}-\u{1F64F}]/gu, '') // emoticons
            .replace(/[\u{1F300}-\u{1F5FF}]/gu, '') // symbols & pictographs
            .replace(/[\u{1F680}-\u{1F6FF}]/gu, '') // transport & map
            .replace(/[\u{2600}-\u{26FF}]/gu, '') // misc symbols
            .replace(/[\u{2700}-\u{27BF}]/gu, '')
        ); // dingbats
      }

      if (type === 'email') {
        return value
          .trim() // remove leading/trailing spaces
          .replace(/\s+/g, '') // remove any remaining spaces
          .replace(/[^a-zA-Z0-9@._+-]/g, '');
        // only valid email chars
      }

      if (type === 'number') {
        let numeric = value.replace(/[^0-9.]/g, '');
        if (numeric.startsWith('0') && numeric.length > 1 && !numeric.includes('.')) {
          numeric = numeric.replace(/^0+/, '');
        }
        if (numeric.includes('.')) {
          const parts = numeric.split('.');
          numeric = parts[0] + '.' + parts[1].slice(0, 2);
        }
        return numeric;
      }

      // Other inputs
      return value
        .replace(/^[ ]+/, '') // no leading space
        .replace(/[^\w\s-]/g, ''); // only word chars, spaces, dash
    };

    const getLabelSuffix = () => {
      if (labelType === 'required') return <span className="ml-0.5 text-btnColor">*</span>;
      if (labelType === 'optional')
        return <span className="ml-1 text-neutralDefault">{t('optional')}</span>;
      return null;
    };
    const variantStyles = {
      primary: 'border-neutralLight bg-neutralWhite text-neutralUltraDark',
      secondary: 'border-neutralExtraLight bg-neutralUltraLight text-neutralDark',
    };

    // Determine input type for password toggle

    const isPasswordType = type === 'password';
    const isEmailType = type === 'email';
    const inputType = isPasswordType && showPassword ? 'text' : type;
    return (
      <div
        data-testid={`custom-input-${name || inputId}`}
        className={cn('space-y-1', containerClassName)}
      >
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'bodySm flex items-center text-sm font-medium uppercase text-neutralUltraDark',
              labelClassName
            )}
          >
            {label}
            {getLabelSuffix()}
          </label>
        )}

        <div className="relative">
          {startAdornment && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutralMedium">
              {startAdornment}
            </div>
          )}

          <input
            id={inputId}
            name={name}
            type={inputType}
            placeholder={placeholder}
            defaultValue={defaultValue}
            value={internalValue}
            onKeyDown={(e) => {
              if (type === 'number' && (e.key === '-' || e.key === 'e')) {
                e.preventDefault();
              }
              if ((inputType === 'email' || inputType === 'password') && e.key === ' ') {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const raw = e.target.value;
              const sanitized = sanitizeInput(raw, type, disableSanitize);
              setInternalValue(sanitized);

              if (onChange) {
                onChange({
                  ...e,
                  target: {
                    ...e.target,
                    value: sanitized,
                  },
                } as React.ChangeEvent<HTMLInputElement>);
              }
            }}
            className={cn(
              'bodyMd flex w-full rounded-xl border px-3 py-2',
              'h-11 lg:h-12', // Responsive height: 44px mobile/tablet (touch-friendly), 48px desktop
              'ring-offset-background placeholder:text-neutralDefault',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primaryDefault focus-visible:ring-offset-1',
              props.readOnly && 'cursor-not-allowed opacity-50',
              startAdornment && 'pl-10',
              (endAdornment || isPasswordType) && 'pr-10',
              error && 'border !border-btnColor focus-visible:ring-transparent',
              variantStyles[variant],
              className
            )}
            ref={ref}
            {...props}
          />

          {/* Password view/hide icon */}
          {isPasswordType && (
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutralMedium focus:outline-none"
              tabIndex={-1}
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          )}

          {endAdornment && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-neutralMedium">
              {endAdornment}
            </div>
          )}
        </div>

        {error && errorMessage ? (
          <p className="bodyXs text-btnColor">{errorMessage}</p>
        ) : helperText ? (
          <p className="bodyXs text-neutralDefault">{helperText}</p>
        ) : null}
      </div>
    );
  }
);
CustomInput.displayName = 'CustomInput';
export { CustomInput };
